let installed = false;

const DEFAULT_MAX_EVENTS = 200;

/** @typedef {{ ts: number, level: 'log'|'info'|'warn'|'error', args: any[] }} ConsoleEvent */

/** @type {ConsoleEvent[]} */
let buffer = [];
let maxEvents = DEFAULT_MAX_EVENTS;

function pushEvent(level, args) {
  try {
    buffer.push({ ts: Date.now(), level, args: Array.from(args || []) });
    if (buffer.length > maxEvents) buffer = buffer.slice(buffer.length - maxEvents);
  } catch {
    // best-effort only
  }
}

function patchConsoleMethod(level) {
  const original = console[level];
  if (typeof original !== 'function') return () => {};
  console[level] = (...args) => {
    pushEvent(level, args);
    return original(...args);
  };
  return () => {
    console[level] = original;
  };
}

/**
 * Installs console + global error capture once.
 * This captures app-emitted logs from this point onward (not the full DevTools history).
 */
export function installConsoleCapture({ max = DEFAULT_MAX_EVENTS } = {}) {
  if (installed) return;
  installed = true;
  maxEvents = typeof max === 'number' && max > 0 ? Math.floor(max) : DEFAULT_MAX_EVENTS;

  const unpatches = [
    patchConsoleMethod('log'),
    patchConsoleMethod('info'),
    patchConsoleMethod('warn'),
    patchConsoleMethod('error'),
  ];

  const onError = (event) => {
    try {
      const msg = event?.message || 'Unhandled error';
      const src = event?.filename ? `${event.filename}:${event.lineno || 0}:${event.colno || 0}` : '';
      pushEvent('error', [msg, src].filter(Boolean).join(' '));
    } catch (e) {
      void e;
    }
  };
  const onUnhandledRejection = (event) => {
    try {
      const reason = event?.reason;
      if (reason instanceof Error) {
        pushEvent('error', [`Unhandled promise rejection: ${reason.message}`, reason.stack || '']);
      } else {
        pushEvent('error', ['Unhandled promise rejection:', reason]);
      }
    } catch (e) {
      void e;
    }
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  // If needed later: return uninstall. For now we keep it installed.
  void unpatches;
}

/** Returns a JSON-safe snapshot of the last captured events. */
export function getConsoleTail({ limit = DEFAULT_MAX_EVENTS } = {}) {
  const n = typeof limit === 'number' && limit > 0 ? Math.floor(limit) : DEFAULT_MAX_EVENTS;
  const tail = buffer.slice(Math.max(0, buffer.length - n));
  // normalize args to improve JSON export readability
  return tail.map((e) => ({
    ts: e.ts,
    level: e.level,
    args: (e.args || []).map((a) => {
      if (a instanceof Error) return { name: a.name, message: a.message, stack: a.stack || '' };
      if (typeof a === 'string') return a;
      try {
        return JSON.parse(JSON.stringify(a));
      } catch {
        return String(a);
      }
    }),
  }));
}

export function clearConsoleCapture() {
  buffer = [];
}

